const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT || 'http://localhost:8085/kzc';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get JWT token from localStorage
  getToken() {
    return localStorage.getItem('token');
  }

  // Get headers with Authorization if token exists
  getHeaders(additionalHeaders = {}) {
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...additionalHeaders,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Get headers for FormData (no Content-Type for FormData)
  getFormDataHeaders() {
    const token = this.getToken();
    const headers = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Handle API errors
  handleError(error) {
    console.error('API Error:', error);
    
    // If it's a 401/403, clear token and redirect to login
    if (error.status === 401 || error.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // You can dispatch an event or use a global state for redirect
      window.dispatchEvent(new Event('auth-expired'));
    }
    
    return Promise.reject(error);
  }

  // Generic fetch method
  async fetch(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.getHeaders(options.headers);
    
    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle unauthorized
      if (response.status === 401 || response.status === 403) {
        this.handleError({ status: response.status, message: 'Unauthorized' });
        return response; 
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      // For DELETE or 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      // Parse JSON response
      return await response.json();
    } catch (error) {
      return this.handleError(error);
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.fetch(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.fetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.fetch(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  // POST with FormData (for file uploads)
  async postFormData(endpoint, formData, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.getFormDataHeaders();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...headers,
          ...options.headers,
        },
        body: formData,
        ...options,
      });

      if (response.status === 401 || response.status === 403) {
        this.handleError({ status: response.status, message: 'Unauthorized' });
        return response;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Specific API methods for your application
  auth = {
    login: (username, password) => 
      this.post('/api/auth/login', { userName: username, password }),
    
    register: (userData) => 
      this.post('/api/auth/register', userData),
    
    changePassword: (oldPassword, newPassword) =>
      this.post('/api/auth/change-password', { oldPassword, newPassword }),
  };

 // services/api.js - Update getCurrentUserId method
getCurrentUserId() {
  try {
    const userStr = localStorage.getItem('user');
    
    if (!userStr) {
      console.warn("⚠️ No user found in localStorage");
      return null;
    }
    
    const user = JSON.parse(userStr);
    
    // If user object already has ID, use it
    if (user.id) {
      return user.id;
    }
    
    // If not, try to decode JWT token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const decoded = JSON.parse(jsonPayload);
        
        return decoded.userId || decoded.id || decoded.user?.id;
      } catch (e) {
        console.error('Error decoding JWT:', e);
      }
    }
    
    return null;
  } catch (e) {
    console.error('Error parsing user from localStorage:', e);
    return null;
  }
}

  registration = {
    create: (registrationData) =>
      this.postFormData('/api/registrations', registrationData),
    
    getAll: () => this.get('/api/registrations'),
    
    getMyRegistrations: (mobile) => {
      return this.get(`/api/registrations/my/${mobile}`);
    },
    
    getMyAddRegistrations: (mobile) => {
      return this.get(`/api/additional-payment/my/${mobile}`);
    },
    getAllAddRegistrations: () => this.get('/api/all-add-payment'),

    getById: (id) => this.get(`/api/registrations/view/${id}`),

    update: async (id, formData) => {
      const url = `${this.baseURL}/api/registrations/update/${id}`;
      const headers = this.getFormDataHeaders();
      
      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: headers,
          body: formData,
        });
  
        if (response.status === 401 || response.status === 403) {
          this.handleError({ status: response.status, message: 'Unauthorized' });
          return response;
        }
  
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
        }
  
        return await response.json();
      } catch (error) {
        return this.handleError(error);
      }
    },
    updateReg: async (id, formData) => {
      const url = `${this.baseURL}/api/registration/update/${id}`;
      const headers = this.getFormDataHeaders();
      
      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: headers,
          body: formData,
        });
  
        if (response.status === 401 || response.status === 403) {
          this.handleError({ status: response.status, message: 'Unauthorized' });
          return response;
        }
  
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
        }
  
        return await response.json();
      } catch (error) {
        return this.handleError(error);
      }
    },
    delete: (id) => this.delete(`/api/registrations/${id}`),
    
    getPhoto: (id) => `${this.baseURL}/api/registrations/${id}/photo`,
  };

  directory = {
    create: (directoryData) =>
      this.post('/api/directories', directoryData),
  
    getAll: () => this.get('/api/directories'),
    getById: (id) => this.get(`/api/directories/${id}`),
    updateById: (id, data) => this.put(`/api/directories/${id}`, data),
    delete: (id) => this.delete(`/api/directories/${id}`),
    searchByMobile: (mobile) => this.get(`/api/directories/mobile/${mobile}`),
  };
  

  batchPricing = {
    getAll: () => this.get('/api/batch-pricing'),
  };

  lookup = {
    getTShirtByGender: (gender) => this.get(`/api/lookup/tShirt/${gender}`),
    getMessage: () => this.get('/api/lookup/tShirt/Message'),
    getRegistrationStatus: () => this.get('/api/lookup/status'),
    updateRegistrationStatus: (active) => 
      this.put(`/api/lookup/status?active=${active}`, {}),
  };

  payment = {
    create: (paymentData) => 
      this.post('/api/shurjo/create-payment', paymentData),

    createAdditional: (paymentData) => 
      this.post('/api/shurjo/create-additional-payment', paymentData),
    
    verify: (orderId) => 
      this.post('/api/shurjo/verify-payment', { order_id: orderId }),
  };

  test = {
    ping: () => this.get('/api/test/ping'),
    echo: (message) => this.post('/api/test/echo', { message }),
  };
}

// Create a singleton instance
const api = new ApiService();

export default api;